import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Dialog, Form, InputNumber } from 'tdesign-react';
import styled from 'styled-components';
import { fetchPolices } from '@/slices/namespaceSlice';
import { deal70101, isEmpty } from '@/utils/common';
import { updateLimitPolicy } from '@/services/namespaceService';
import ColTip from '@/components/Tip';
import useDisableButton from '@/components/DisableWrapper/useDisableButton';

const { FormItem } = Form;

function Modal({ visible, handleClose, detail, commonProps, fetchPolices }) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const confirmBtn = useDisableButton({ loading: submitting, content: '确定' });

  useEffect(() => {
    if (visible) {
      form.reset();
      form.setFieldsValue(detail);
    }
  }, [form, visible, detail]);

  const onCancel = () => {
    handleClose();
  };

  const handleSubmit = async ({ validateResult, fields }) => {
    if (validateResult === true) {
      setSubmitting(true);
      try {
        const { meta } = await updateLimitPolicy({
          ...commonProps,
          ...fields,
        });
        // 主集群创建的资源已在灾备集群存在
        if (meta.code === 70101) {
          deal70101(meta.publicMessage, () => handleSubmit({ validateResult, fields }), {
            url: '/cluster/namespace/list',
            namespace: commonProps.namespace,
          });

          return;
        }
        await fetchPolices(commonProps);
        handleClose();
      } catch (error) {
        console.trace(error);
        throw error;
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <Root
      onConfirm={() => form.submit()}
      confirmBtn={confirmBtn}
      visible={visible}
      header='限流策略'
      width={550}
      placement='center'
      onClose={onCancel}
    >
      <Form form={form} onSubmit={handleSubmit} style={{ width: '84%' }} labelWidth={140}>
        <Title>
          {
            <ColTip
              content={
                <>
                  <strong>生产限流</strong>
                  <li>1、生产限流是对每个Topic的单个分区进行限制。</li>
                  <li>2、默认为-1，代表禁用。</li>
                  <li>3、限流存在一定的误差，不保证精确限流。</li>
                  <p></p>
                  <li>例如配置为1000/s，Topic分区为8，则每个分区生产流量限制为1000/s。</li>
                </>
              }
            >
              生产限流
            </ColTip>
          }
        </Title>
        <FormItem
          name='publishThrottlingRateInByte'
          label='吞吐'
          rules={[{ validator: (val) => isEmpty(val) || val >= -1, message: '请输入不小于-1的值' }]}
        >
          <InputNumber theme='normal' suffix='Byte/s' decimalPlaces={0} />
        </FormItem>
        <FormItem
          name='publishThrottlingRateInMsg'
          label='速率'
          rules={[{ validator: (val) => isEmpty(val) || val >= -1, message: '请输入不小于-1的值' }]}
        >
          <InputNumber theme='normal' suffix='条/s' decimalPlaces={0} />
        </FormItem>

        <Title>
          {
            <ColTip
              content={
                <>
                  <strong>消费限流</strong>
                  <li>1、消费限流是对每个Topic的单个分区进行限制。</li>
                  <li>2、默认为-1，代表禁用。</li>
                  <li>3、限流存在一定的误差，不保证精确限流。</li>
                  <p></p>
                  <li>例如配置为1000/s，Topic分区为8，则每个分区消费流量限制为1000/s。</li>
                </>
              }
            >
              消费限流
            </ColTip>
          }
        </Title>
        <FormItem
          name='dispatchThrottlingRateBytes'
          label='吞吐'
          rules={[{ validator: (val) => isEmpty(val) || val >= -1, message: '请输入不小于0的值' }]}
        >
          <InputNumber suffix='Byte/s' theme='normal' decimalPlaces={0} />
        </FormItem>
        <FormItem
          name='dispatchThrottlingRateMsg'
          label='速率'
          rules={[{ validator: (val) => isEmpty(val) || val >= -1, message: '请输入不小于0的值' }]}
        >
          <InputNumber suffix='条/s' theme='normal' decimalPlaces={0} />
        </FormItem>
        <FormItem
          name='dispatchRatePeriodInSeconds'
          label='统计间隔'
          rules={[{ validator: (val) => isEmpty(val) || val > 0, message: '请输入不小于0的值' }]}
        >
          <InputNumber suffix='秒' theme='normal' decimalPlaces={0} />
        </FormItem>

        <Title>
          {
            <ColTip
              content={
                <>
                  <strong>订阅速率</strong>
                  <li>1、消费端建立链接并订阅的速率。</li>
                  <li>2、默认为-1，代表禁用。</li>
                </>
              }
            >
              订阅速率
            </ColTip>
          }
        </Title>

        <FormItem
          name='subscribeThrottlingRate'
          label='速率'
          rules={[{ validator: (val) => isEmpty(val) || val >= -1, message: '请输入不小于0的值' }]}
        >
          <InputNumber suffix='个/s' theme='normal' decimalPlaces={0} />
        </FormItem>

        <FormItem
          name='subscribeRatePeriodInSecond'
          label='统计间隔'
          rules={[{ validator: (val) => isEmpty(val) || val > 0, message: '请输入不小于0的值' }]}
        >
          <InputNumber suffix='秒' theme='normal' decimalPlaces={0} />
        </FormItem>
      </Form>
    </Root>
  );
}

export default connect(null, {
  fetchPolices,
})(Modal);

const Title = styled(ColTip)`
  font-weight: bold;
  padding-left: 6px;
  margin: 8px 0;
`;

const Root = styled(Dialog)`
  .t-input-number {
    width: auto;
    flex: 1;
  }
  .t-input__suffix {
    color: var(--td-font-gray-2);
  }
`;
